import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  Plus,
  MoreHorizontal,
  Edit,
  Shield,
  Download,
  UserCheck,
  UserX,
  Loader2,
  Mail,
  Key,
  Eye,
  EyeOff,
  Ban,
  CheckCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/layout/PageHeader";
import { Users, Phone, Building2, ShieldCheck, Lock } from "lucide-react";
import { usePermissions, AppRole } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { useTableSort } from "@/hooks/useTableSort";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { SortResetButton } from "@/components/ui/sort-reset-button";

interface UserProfile {
  id: string;
  user_id: string;
  full_name: string | null;
  phone: string | null;
  department: string | null;
  avatar_url: string | null;
  created_at: string;
  email?: string;
  roles: AppRole[];
  is_banned?: boolean;
  is_approved?: boolean;
}

const roleColors: Record<string, string> = {
  admin: "bg-primary/20 text-primary",
  manager: "bg-success/20 text-success",
  analyst: "bg-info/20 text-info",
  operator: "bg-muted text-muted-foreground",
  user: "bg-warning/20 text-warning",
};

const roleLabels: Record<string, string> = {
  admin: "Administrador",
  manager: "Gerente",
  analyst: "Analista",
  operator: "Operador",
  user: "Usuario",
};

const departments = ["TI", "Ventas", "Finanzas", "Operaciones", "RRHH", "Marketing"];

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<AppRole>("user");
  const [inviting, setInviting] = useState(false);

  // Password change states
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState<UserProfile | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Own password change states
  const [ownPasswordDialogOpen, setOwnPasswordDialogOpen] = useState(false);
  const [ownNewPassword, setOwnNewPassword] = useState("");
  const [ownConfirmPassword, setOwnConfirmPassword] = useState("");
  const [showOwnPassword, setShowOwnPassword] = useState(false);
  const [changingOwnPassword, setChangingOwnPassword] = useState(false);

  // Deactivate/Activate states
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<UserProfile | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  // Approve user states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [userToApprove, setUserToApprove] = useState<UserProfile | null>(null);
  const [approving, setApproving] = useState(false);

  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    department: "",
    role: "user" as AppRole,
  });

  const { isAdmin, canAccess } = usePermissions();
  const { toast } = useToast();
  const { workspace } = useWorkspace();

  useEffect(() => {
    if (workspace?.id) {
      fetchUsers();
    }
  }, [isAdmin, workspace?.id]);

  const fetchUsers = async () => {
    if (!workspace?.id) return;
    
    setLoading(true);
    try {
      // Get workspace members and owner
      const workspaceUserIds = new Set<string>();
      
      // Add workspace owner
      if (workspace.owner_id) {
        workspaceUserIds.add(workspace.owner_id);
      }
      
      // Fetch workspace members
      const { data: members, error: membersError } = await supabase
        .from("workspace_members")
        .select("user_id")
        .eq("workspace_id", workspace.id);
      
      if (membersError) {
        console.error("Error fetching workspace members:", membersError);
      } else if (members) {
        members.forEach((m: any) => workspaceUserIds.add(m.user_id));
      }

      // Fetch profiles only for workspace users
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, user_id, full_name, phone, role, department, avatar_url, two_factor_enabled, created_at, updated_at, is_approved")
        .in("user_id", Array.from(workspaceUserIds))
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Fetch user roles only for workspace users
      const { data: allRoles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .in("user_id", Array.from(workspaceUserIds));

      if (rolesError) throw rolesError;

      // If admin, fetch auth users to get ban status (already filtered by workspace in the edge function)
      let authUsersMap: Record<string, { is_banned: boolean; email: string }> = {};
      if (isAdmin) {
        try {
          const { data, error } = await supabase.functions.invoke("list-users");
          if (!error && data?.users) {
            authUsersMap = data.users.reduce((acc: Record<string, { is_banned: boolean; email: string }>, user: any) => {
              acc[user.id] = { is_banned: user.is_banned, email: user.email };
              return acc;
            }, {});
          }
        } catch (e) {
          console.error("Error fetching auth users:", e);
        }
      }

      // Combine profiles with roles and auth status
      const usersWithRoles: UserProfile[] = (profiles || []).map((profile: any) => {
        const userRoles = allRoles
          ?.filter((r) => r.user_id === profile.user_id)
          .map((r) => r.role as AppRole) || [];
        
        const authInfo = authUsersMap[profile.user_id];
        
        return {
          ...profile,
          roles: userRoles.length > 0 ? userRoles : ["user"],
          is_banned: authInfo?.is_banned || false,
          is_approved: profile.is_approved ?? false,
          email: authInfo?.email,
        };
      });

      setUsers(usersWithRoles);
    } catch (error: any) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los usuarios",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user: UserProfile) => {
    setEditingUser(user);
    setFormData({
      full_name: user.full_name || "",
      phone: user.phone || "",
      department: user.department || "",
      role: user.roles[0] || "user",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingUser) return;

    setSaving(true);
    try {
      // Update profile
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          full_name: formData.full_name || null,
          phone: formData.phone || null,
          department: formData.department || null,
        })
        .eq("user_id", editingUser.user_id);

      if (profileError) throw profileError;

      // Update role if admin and role changed
      if (isAdmin && formData.role !== editingUser.roles[0]) {
        // Delete existing roles for user
        await supabase
          .from("user_roles")
          .delete()
          .eq("user_id", editingUser.user_id);

        // Insert new role
        const { error: roleError } = await supabase
          .from("user_roles")
          .insert({
            user_id: editingUser.user_id,
            role: formData.role,
          });

        if (roleError) throw roleError;
      }

      toast({ title: "Usuario actualizado" });
      setDialogOpen(false);
      fetchUsers();
    } catch (error: any) {
      console.error("Error saving user:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar el usuario",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast({
        title: "Error",
        description: "El email es requerido",
        variant: "destructive",
      });
      return;
    }

    setInviting(true);
    try {
      const { error } = await supabase.functions.invoke("invite-user", {
        body: { email: inviteEmail, role: inviteRole },
      });

      if (error) throw error;

      toast({ 
        title: "Invitación enviada",
        description: `Se ha enviado una invitación a ${inviteEmail}`,
      });
      setInviteDialogOpen(false);
      setInviteEmail("");
      setInviteRole("user");
    } catch (error: any) {
      console.error("Error inviting user:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo enviar la invitación",
        variant: "destructive",
      });
    } finally {
      setInviting(false);
    }
  };

  // Admin change password for any user
  const openPasswordDialog = (user: UserProfile) => {
    setPasswordUser(user);
    setNewPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setPasswordDialogOpen(true);
  };

  const handleAdminChangePassword = async () => {
    if (!passwordUser) return;

    if (newPassword.length < 6) {
      toast({
        title: "Error",
        description: "La contraseña debe tener al menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Las contraseñas no coinciden",
        variant: "destructive",
      });
      return;
    }

    setChangingPassword(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-update-user", {
        body: {
          userId: passwordUser.user_id,
          action: "change_password",
          newPassword,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "Contraseña actualizada", description: `Se actualizó la contraseña de ${passwordUser.full_name || "usuario"}` });
      setPasswordDialogOpen(false);
    } catch (error: any) {
      console.error("Error changing password:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo cambiar la contraseña",
        variant: "destructive",
      });
    } finally {
      setChangingPassword(false);
    }
  };

  // User changes own password
  const handleChangeOwnPassword = async () => {
    if (ownNewPassword.length < 6) {
      toast({
        title: "Error",
        description: "La contraseña debe tener al menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    if (ownNewPassword !== ownConfirmPassword) {
      toast({
        title: "Error",
        description: "Las contraseñas no coinciden",
        variant: "destructive",
      });
      return;
    }

    setChangingOwnPassword(true);
    try {
      const { data, error } = await supabase.functions.invoke("change-own-password", {
        body: { newPassword: ownNewPassword },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "Contraseña actualizada", description: "Tu contraseña ha sido actualizada correctamente" });
      setOwnPasswordDialogOpen(false);
      setOwnNewPassword("");
      setOwnConfirmPassword("");
    } catch (error: any) {
      console.error("Error changing own password:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo cambiar la contraseña",
        variant: "destructive",
      });
    } finally {
      setChangingOwnPassword(false);
    }
  };

  // Deactivate/Activate user
  const openDeactivateDialog = (user: UserProfile) => {
    setUserToDeactivate(user);
    setDeactivateDialogOpen(true);
  };

  const handleToggleUserStatus = async () => {
    if (!userToDeactivate) return;

    setDeactivating(true);
    try {
      const action = userToDeactivate.is_banned ? "activate" : "deactivate";
      const { data, error } = await supabase.functions.invoke("admin-update-user", {
        body: {
          userId: userToDeactivate.user_id,
          action,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const actionLabel = action === "deactivate" ? "desactivado" : "activado";
      toast({ title: `Usuario ${actionLabel}`, description: `${userToDeactivate.full_name || "Usuario"} ha sido ${actionLabel}` });
      setDeactivateDialogOpen(false);
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.user_id === userToDeactivate.user_id 
          ? { ...u, is_banned: action === "deactivate" } 
          : u
      ));
    } catch (error: any) {
      console.error("Error toggling user status:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo cambiar el estado del usuario",
        variant: "destructive",
      });
    } finally {
      setDeactivating(false);
    }
  };

  // Approve user
  const openApproveDialog = (user: UserProfile) => {
    setUserToApprove(user);
    setApproveDialogOpen(true);
  };

  const handleApproveUser = async () => {
    if (!userToApprove) return;

    setApproving(true);
    try {
      const newApprovalStatus = !userToApprove.is_approved;
      const { error } = await supabase
        .from("profiles")
        .update({ is_approved: newApprovalStatus })
        .eq("user_id", userToApprove.user_id);

      if (error) throw error;

      const actionLabel = newApprovalStatus ? "aprobado" : "revocado";
      toast({ 
        title: `Acceso ${actionLabel}`, 
        description: `${userToApprove.full_name || "Usuario"} ha sido ${actionLabel}` 
      });
      setApproveDialogOpen(false);
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.user_id === userToApprove.user_id 
          ? { ...u, is_approved: newApprovalStatus } 
          : u
      ));
    } catch (error: any) {
      console.error("Error approving user:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo cambiar el estado de aprobación",
        variant: "destructive",
      });
    } finally {
      setApproving(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.department?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment =
      departmentFilter === "all" || user.department === departmentFilter;
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && !user.is_banned && user.is_approved) ||
      (statusFilter === "inactive" && user.is_banned) ||
      (statusFilter === "pending" && !user.is_approved && !user.is_banned);
    return matchesSearch && matchesDepartment && matchesStatus;
  });

  const { sortState, handleSort, clearSort, sortedData, isSorted } = useTableSort(filteredUsers);


  const canEdit = isAdmin || canAccess("users", "edit");

  const maxUsers = workspace?.subscription?.max_users || 3;
  const currentUserCount = users.length;
  const remainingSlots = Math.max(0, maxUsers - currentUserCount);
  const slotsUsedPercent = Math.min(100, (currentUserCount / maxUsers) * 100);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader title="Usuarios" description="Gestiona los usuarios y sus permisos">
          <Button variant="outline" size="sm" onClick={() => setOwnPasswordDialogOpen(true)}>
            <Key className="h-4 w-4 mr-2" />
            Cambiar mi Contraseña
          </Button>
          <Button variant="secondary" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          {isAdmin && (
            <Button 
              className="shadow-lg" 
              onClick={() => setInviteDialogOpen(true)}
              disabled={remainingSlots <= 0}
            >
              <Plus className="mr-2 h-4 w-4" />
              Invitar Usuario
            </Button>
          )}
        </PageHeader>

        {/* User slots counter */}
        {isAdmin && (
          <div className="chart-container animate-slide-in-up">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">
                    {currentUserCount} / {maxUsers} usuarios
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {remainingSlots > 0
                      ? `${remainingSlots} plaza${remainingSlots !== 1 ? "s" : ""} disponible${remainingSlots !== 1 ? "s" : ""}`
                      : "Sin plazas disponibles — mejora tu plan"}
                  </p>
                </div>
              </div>
              <div className="flex-1 max-w-xs">
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all",
                      slotsUsedPercent >= 100
                        ? "bg-destructive"
                        : slotsUsedPercent >= 80
                          ? "bg-warning"
                          : "bg-primary"
                    )}
                    style={{ width: `${slotsUsedPercent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="chart-container animate-slide-in-up">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, email o departamento..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-secondary border-border"
              />
            </div>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-40 bg-secondary border-border">
                <SelectValue placeholder="Departamento" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="all">Todos</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-secondary border-border">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="active">
                    <div className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-emerald-500" />
                      Activos
                    </div>
                  </SelectItem>
                  <SelectItem value="pending">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-warning" />
                      Pendientes
                    </div>
                  </SelectItem>
                  <SelectItem value="inactive">
                    <div className="flex items-center gap-2">
                      <UserX className="h-4 w-4 text-destructive" />
                      Inactivos
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Users Table */}
        <div className="chart-container animate-slide-in-up" style={{ animationDelay: "100ms" }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <table className="data-table min-w-[900px]">
                <thead>
                  <tr>
                    <SortableTableHead field="full_name" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Usuario</SortableTableHead>
                    <SortableTableHead field="role" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Rol</SortableTableHead>
                    <SortableTableHead field="department" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Departamento</SortableTableHead>
                    <SortableTableHead field="phone" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Teléfono</SortableTableHead>
                    <SortableTableHead field="status" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Estado</SortableTableHead>
                    <SortableTableHead field="created_at" currentField={sortState.field} direction={sortState.direction} onSort={handleSort}>Registrado</SortableTableHead>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((user, index) => (
                    <tr
                      key={user.id}
                      className="animate-slide-in-left"
                      style={{ animationDelay: `${200 + index * 50}ms` }}
                    >
                      <td>
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "h-10 w-10 rounded-full flex items-center justify-center",
                            user.is_banned ? "bg-destructive/20" : "bg-primary/20"
                          )}>
                            <span className={cn(
                              "text-sm font-medium",
                              user.is_banned ? "text-destructive" : "text-primary"
                            )}>
                              {user.full_name
                                ?.split(" ")
                                .map((n) => n[0])
                                .join("") || "U"}
                            </span>
                          </div>
                          <div>
                            <p className={cn("font-medium", user.is_banned && "text-muted-foreground")}>{user.full_name || "Sin nombre"}</p>
                            <p className="text-sm text-muted-foreground">
                              {user.email || `ID: ${user.user_id.slice(0, 8)}...`}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {user.roles.map((role) => (
                            <span
                              key={role}
                              className={cn(
                                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                                roleColors[role]
                              )}
                            >
                              {roleLabels[role]}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>{user.department || "-"}</td>
                      <td>{user.phone || "-"}</td>
                      <td>
                        {user.is_banned ? (
                          <Badge variant="destructive">
                            <UserX className="h-3 w-3 mr-1" />
                            Desactivado
                          </Badge>
                        ) : !user.is_approved ? (
                          <Badge 
                            variant="outline"
                            className="bg-warning/20 text-warning border-warning/50"
                          >
                            <Mail className="h-3 w-3 mr-1" />
                            Pendiente
                          </Badge>
                        ) : (
                          <Badge 
                            variant="default"
                            className="bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/30"
                          >
                            <UserCheck className="h-3 w-3 mr-1" />
                            Activo
                          </Badge>
                        )}
                      </td>
                      <td className="text-muted-foreground">
                        {new Date(user.created_at).toLocaleDateString("es-ES")}
                      </td>
                      <td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-popover border-border">
                            {canEdit && (
                              <DropdownMenuItem onClick={() => openEditDialog(user)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                            )}
                            {isAdmin && (
                              <>
                                <DropdownMenuItem onClick={() => openEditDialog(user)}>
                                  <Shield className="h-4 w-4 mr-2" />
                                  Cambiar Rol
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => openPasswordDialog(user)}>
                                  <Key className="h-4 w-4 mr-2" />
                                  Cambiar Contraseña
                                </DropdownMenuItem>
                                {user.user_id !== currentUser?.id && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => openApproveDialog(user)}
                                      className={user.is_approved ? "text-destructive" : "text-emerald-600"}
                                    >
                                      {user.is_approved ? (
                                        <>
                                          <Ban className="h-4 w-4 mr-2" />
                                          Desactivar Cuenta
                                        </>
                                      ) : (
                                        <>
                                          <CheckCircle className="h-4 w-4 mr-2" />
                                          Activar Cuenta
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                  {sortedData.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-muted-foreground">
                        No se encontraron usuarios
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-border pt-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Mostrando {filteredUsers.length} de {users.length} usuarios
            </p>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Editar Usuario</DialogTitle>
            <DialogDescription>
              Modifica los datos del usuario
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Users className="h-4 w-4" /> Información Personal
              </h3>
              <div className="space-y-2">
                <Label className="text-xs">Nombre Completo</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Teléfono</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-9"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Departamento</Label>
                  <Select
                    value={formData.department}
                    onValueChange={(value) => setFormData({ ...formData, department: value })}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((dept) => (
                        <SelectItem key={dept} value={dept}>
                          {dept}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {isAdmin && (
              <div className="rounded-lg border border-border bg-card p-4 space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" /> Permisos
                </h3>
                <div className="space-y-2">
                  <Label className="text-xs">Rol</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value as AppRole })}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="manager">Gerente</SelectItem>
                      <SelectItem value="analyst">Analista</SelectItem>
                      <SelectItem value="operator">Operador</SelectItem>
                      <SelectItem value="user">Usuario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Invitar Usuario</DialogTitle>
            <DialogDescription>
              Envía una invitación por email para un nuevo usuario
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Mail className="h-4 w-4" /> Datos de Invitación
              </h3>
              <div className="space-y-2">
                <Label className="text-xs">Email *</Label>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="usuario@ejemplo.com"
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Rol inicial</Label>
                <Select
                  value={inviteRole}
                  onValueChange={(value) => setInviteRole(value as AppRole)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="manager">Gerente</SelectItem>
                    <SelectItem value="analyst">Analista</SelectItem>
                    <SelectItem value="operator">Operador</SelectItem>
                    <SelectItem value="user">Usuario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setInviteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleInviteUser} disabled={inviting}>
              {inviting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Mail className="h-4 w-4 mr-2" />
              Enviar Invitación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Cambiar Contraseña</DialogTitle>
            <DialogDescription>
              Establece una nueva contraseña para {passwordUser?.full_name || "el usuario"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Lock className="h-4 w-4" /> Nueva Contraseña
              </h3>
              <div className="space-y-2">
                <Label className="text-xs">Contraseña</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="h-9 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Confirmar Contraseña</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="h-9"
                />
              </div>
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-sm text-destructive">Las contraseñas no coinciden</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setPasswordDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAdminChangePassword} 
              disabled={changingPassword || newPassword.length < 6 || newPassword !== confirmPassword}
            >
              {changingPassword && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Key className="h-4 w-4 mr-2" />
              Cambiar Contraseña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Own Password Change Dialog */}
      <Dialog open={ownPasswordDialogOpen} onOpenChange={setOwnPasswordDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Cambiar mi Contraseña</DialogTitle>
            <DialogDescription>
              Establece una nueva contraseña para tu cuenta
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Lock className="h-4 w-4" /> Nueva Contraseña
              </h3>
              <div className="space-y-2">
                <Label className="text-xs">Contraseña</Label>
                <div className="relative">
                  <Input
                    type={showOwnPassword ? "text" : "password"}
                    value={ownNewPassword}
                    onChange={(e) => setOwnNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="h-9 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowOwnPassword(!showOwnPassword)}
                  >
                    {showOwnPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Confirmar Contraseña</Label>
                <Input
                  type={showOwnPassword ? "text" : "password"}
                  value={ownConfirmPassword}
                  onChange={(e) => setOwnConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="h-9"
                />
              </div>
              {ownNewPassword && ownConfirmPassword && ownNewPassword !== ownConfirmPassword && (
                <p className="text-sm text-destructive">Las contraseñas no coinciden</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setOwnPasswordDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleChangeOwnPassword} 
              disabled={changingOwnPassword || ownNewPassword.length < 6 || ownNewPassword !== ownConfirmPassword}
            >
              {changingOwnPassword && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              <Key className="h-4 w-4 mr-2" />
              Cambiar Contraseña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate/Activate Confirmation Dialog */}
      <AlertDialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {userToDeactivate?.is_banned ? "Activar Usuario" : "Desactivar Usuario"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {userToDeactivate?.is_banned 
                ? `¿Estás seguro de que deseas activar la cuenta de ${userToDeactivate?.full_name || "este usuario"}? El usuario podrá volver a iniciar sesión.`
                : `¿Estás seguro de que deseas desactivar la cuenta de ${userToDeactivate?.full_name || "este usuario"}? El usuario no podrá iniciar sesión hasta que se reactive su cuenta.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleUserStatus}
              disabled={deactivating}
              className={userToDeactivate?.is_banned ? "bg-emerald-600 hover:bg-emerald-700" : "bg-destructive hover:bg-destructive/90"}
            >
              {deactivating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {userToDeactivate?.is_banned ? "Activar" : "Desactivar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Activate/Deactivate Account Confirmation Dialog */}
      <AlertDialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {userToApprove?.is_approved ? "Desactivar Cuenta" : "Activar Cuenta"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {userToApprove?.is_approved 
                ? `¿Estás seguro de que deseas desactivar la cuenta de ${userToApprove?.full_name || "este usuario"}? El usuario no podrá acceder al portal hasta que se reactive.`
                : `¿Estás seguro de que deseas activar la cuenta de ${userToApprove?.full_name || "este usuario"}? El usuario podrá acceder al portal.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApproveUser}
              disabled={approving}
              className={userToApprove?.is_approved ? "bg-destructive hover:bg-destructive/90" : "bg-emerald-600 hover:bg-emerald-700"}
            >
              {approving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {userToApprove?.is_approved ? "Desactivar" : "Activar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
